// What a retail park developer asks for, as opposed to what the plot allows.
//
// A retail park is one storey of large boxes in a row (or an L, or a U) at
// the back of the plot, the car park in front of them towards the road, a
// service yard behind them for the lorries, and a few drive-through pads on
// the frontage. The questions at feasibility are how much lettable area the
// plot carries, how many spaces that needs and whether they fit, and what
// the rent roll is worth. Parking, not POT, is usually what binds.

/** Anchor (supermarket, DIY, electronics), medium box (fashion, home, sport), small unit (discount, services). */
export type RetailKind = "anchor" | "medium" | "small";

export interface RetailMixItem {
  kind: RetailKind;
  /** Share of the lettable area, 0–1. */
  share: number;
  /** Target lettable area of one unit, m². */
  area: number;
}

export interface RetailProgramme {
  /** Cornice of the boxes, m: about 7 m clear under the roof structure. */
  height: number;
  /** Depths of the row to try, m. The row has one depth; units differ by width. */
  depths: number[];
  mix: RetailMixItem[];
  /** Drive-through or restaurant pads on the frontage, each on its own lot. */
  pads: number;
  /** Built area of one pad, m². */
  padArea: number;
  /** Paved yard behind the units, measured from the boundary, m. Lorries reverse into the docks across it. */
  serviceDepth: number;
  /** Canopy over the walkway in front of the shops, m. It is a permanent projection: it enters POT. */
  canopyDepth: number;
  /** Drive lane along the shopfronts, between the canopy and the car park, m: drop-off and fire access. */
  frontRoad: number;
  /** Planted strip along the street, m. Pylon and car entrance cross it; parking does not. */
  streetBuffer: number;
  /** One tree island for every this many spaces in a row; 0 = none. */
  treeEvery: number;
  /** What the tenants ask for: spaces per 100 m² of lettable area. The engine sizes for the larger of this and the norm. */
  parkingTarget: number;
  /** Cap on lettable area, m²; 0 = as much as the plot carries. */
  maxGla: number;
  /** Structural bay along the row, m. Party walls between units fall on half-bays. */
  bay: number;
}

export const DEFAULT_RETAIL: RetailProgramme = {
  height: 8,
  depths: [45, 38, 30],
  mix: [
    // order of magnitude of the units let in Romanian retail parks:
    // a supermarket or DIY anchor, mid boxes of 600–1,500 m², small units of 200–500 m²
    { kind: "anchor", share: 0.35, area: 2500 },
    { kind: "medium", share: 0.45, area: 1000 },
    { kind: "small", share: 0.2, area: 400 },
  ],
  pads: 2,
  padArea: 320,
  serviceDepth: 30,
  canopyDepth: 3,
  frontRoad: 7,
  streetBuffer: 6,
  treeEvery: 8,
  parkingTarget: 4,
  maxGla: 0,
  bay: 12,
};

export const KIND_NAME: Record<RetailKind | "pad", string> = { anchor: "Ancoră", medium: "Unitate medie", small: "Unitate mică", pad: "Pad drive-thru" };

/** Lettable over built: party walls and the outer wall. The rest of a box is lettable. */
export const GLA_RATIO = 0.97;
/** Narrowest unit, m: below this a box is a corridor with a shopfront. */
export const MIN_UNIT_WIDTH = 8;
/** Width of a dock door and of a service door, m. */
export const DOCK_WIDTH = 3.5;
export const SERVICE_DOOR = 1.2;
/** Two-way lorry lane from the street to a service yard, m. */
export const TRUCK_LANE = 8;
/** Two-way car entrance from the street, m. */
export const CAR_ACCESS = 7;
/** Drive-through lane round a pad, m. */
export const DRIVE_THRU = 4;
